import React, { useState } from "react";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { PATHIS, PREACHERS, GHARS, savePeople, withHonorific } from "../../data/rssbData";
import { PersonDialog } from "../PersonDialog";
import { GharDirectory } from "../GharDirectory";
import { toast } from "sonner";
import { Search, Users, BookOpen, Plus, Pencil, Trash2, Home } from "lucide-react";

const PersonCard = ({ x, onEdit, onDelete }) => (
  <div
    data-testid={`dir-item-${x.id}`}
    className="animate-fade-up bg-[#fffdf7] border border-[#e3d6b6] rounded-lg p-3 hover:border-[#6e1b1b]/50 transition-colors"
  >
    <div className="flex items-start justify-between gap-2">
      <span className="font-serif-dev font-semibold text-[#3a1212]">{withHonorific(x)}</span>
      <div className="flex items-center gap-1 shrink-0">
        {x.role && <Badge className="bg-[#e3d6b6] text-[#5a2a24] text-[10px]">{x.role}</Badge>}
        <button data-testid={`dir-edit-${x.id}`} onClick={onEdit} className="p-1.5 rounded text-[#6e1b1b] hover:bg-[#f3ead6]" aria-label="edit">
          <Pencil className="w-3.5 h-3.5" />
        </button>
        <button data-testid={`dir-delete-${x.id}`} onClick={onDelete} className="p-1.5 rounded text-[#b91c1c] hover:bg-[#f3ead6]" aria-label="delete">
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
    <div className="text-xs text-[#7a4a3a] mt-1">{x.en}</div>
    <div className="text-xs text-[#9a7a5a] mt-0.5 flex items-center gap-2 flex-wrap font-dev">
      <span>📍 {x.placeHi || "—"}</span>
      {x.mobile && <span data-testid={`dir-mobile-${x.id}`}>📞 {x.mobile}</span>}
    </div>
  </div>
);

export const DirectoryTab = () => {
  const [tab, setTab] = useState("pathis");
  const [q, setQ] = useState("");
  const [, setVer] = useState(0);
  const [dlg, setDlg] = useState({ open: false, person: null });

  const kind = tab === "pathis" ? "pathi" : "preacher";
  const source = tab === "pathis" ? PATHIS : PREACHERS;
  const list = source.filter(
    (x) => !q || x.hi.includes(q) || (x.en || "").toLowerCase().includes(q.toLowerCase()) || (x.placeHi || "").includes(q)
  );

  const commit = (next, msg) => {
    savePeople(kind, next);
    setVer((v) => v + 1);
    toast.success(msg);
  };

  const handleSave = (p) => {
    const exists = source.some((x) => x.id === p.id);
    commit(exists ? source.map((x) => (x.id === p.id ? p : x)) : [...source, p], exists ? "विवरण अपडेट हुआ" : "नया नाम जोड़ा गया");
    setDlg({ open: false, person: null });
  };

  const handleDelete = (p) => {
    if (!window.confirm(`${withHonorific(p)} को सूची से हटाएं?`)) return;
    commit(source.filter((x) => x.id !== p.id), "नाम सूची से हटाया गया");
  };

  const tabBtn = (active) =>
    `px-4 py-2 text-sm font-medium flex items-center gap-2 ${active ? "bg-[#431010] text-white" : "bg-[#fffdf7] text-[#7a4a3a]"}`;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3 items-center">
        <div className="flex rounded-lg overflow-hidden border border-[#e3d6b6]">
          <button data-testid="dir-tab-pathis" onClick={() => setTab("pathis")} className={tabBtn(tab === "pathis")}>
            <BookOpen className="w-4 h-4" /> पाठी ({PATHIS.length})
          </button>
          <button data-testid="dir-tab-preachers" onClick={() => setTab("preachers")} className={tabBtn(tab === "preachers")}>
            <Users className="w-4 h-4" /> SR/SK ({PREACHERS.length})
          </button>
          <button data-testid="dir-tab-ghars" onClick={() => setTab("ghars")} className={tabBtn(tab === "ghars")}>
            <Home className="w-4 h-4" /> सत्संग घर ({GHARS.length})
          </button>
        </div>
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#9a7a5a]" />
          <Input
            data-testid="dir-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="नाम या स्थान खोजें..."
            className="pl-9 bg-[#fffdf7] border-[#e3d6b6] text-[#3a1212] font-dev"
          />
        </div>
        {tab !== "ghars" && (
        <Button
          data-testid="dir-add"
          onClick={() => setDlg({ open: true, person: null })}
          className="bg-gradient-to-b from-[#7a1f1f] to-[#5e1414] border border-[#431010] text-[#f3ead6] font-semibold gap-1 font-dev"
        >
          <Plus className="w-4 h-4" /> नया {tab === "pathis" ? "पाठी" : "SR/SK"} जोड़ें
        </Button>
        )}
      </div>

      {tab === "ghars" ? <GharDirectory q={q} onChange={() => setVer((v) => v + 1)} /> : (
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {list.map((x) => (
          <PersonCard key={x.id} x={x} onEdit={() => setDlg({ open: true, person: x })} onDelete={() => handleDelete(x)} />
        ))}
        {!list.length && <div className="col-span-full text-center text-[#9a7a5a] py-10">कोई परिणाम नहीं मिला</div>}
      </div>
      )}

      <PersonDialog
        open={dlg.open}
        kind={kind}
        person={dlg.person}
        onClose={() => setDlg({ open: false, person: null })}
        onSave={handleSave}
      />
    </div>
  );
};
